package se.mittpass.web.components

import se.mittpass.web.*
import se.mittpass.web.components.*
import se.mittpass.web.pages.*
import se.mittpass.web.viewmodel.*
import se.mittpass.web.style.*


import androidx.compose.runtime.*
import kotlinx.coroutines.MainScope
import kotlinx.coroutines.launch
import org.jetbrains.compose.web.dom.*
import se.mittpass.shared.api.PayrollRecordDto
import se.mittpass.shared.api.UpdatePayrollRecordRequest

private val monthViewScope = MainScope()

@Composable
internal fun PayrollMonthView(
    records: List<PayrollRecordDto>,
    drag: BarDragState?,
    onDragStart: (BarDragState) -> Unit,
    onDragUpdate: (BarDragState) -> Unit,
    onDragEnd: (BarDragState) -> Unit,
    onEditRecord: (PayrollRecordDto) -> Unit,
    onReload: () -> Unit,
    isMobile: Boolean = false
) {
    // Group by userId, sorted by name
    val byPerson = remember(records) {
        records.groupBy { it.userId }
            .entries
            .sortedBy { (_, recs) ->
                listOfNotNull(recs.first().firstName, recs.first().lastName).joinToString(" ")
                    .ifEmpty { recs.first().userEmail }
            }
    }

    val autoExpand = remember { byPerson.size <= 4 }
    val expandedIds = remember { mutableStateMapOf<Long, Boolean>() }
    // Initialize newly seen users without wiping existing expanded states
    LaunchedEffect(byPerson) {
        byPerson.forEach { (uid, _) ->
            if (!expandedIds.containsKey(uid)) {
                expandedIds[uid] = autoExpand
            }
        }
    }

    val allExpanded = byPerson.all { (uid, _) -> expandedIds[uid] == true }

    Div({ style { property("display", "flex"); property("justify-content", "flex-end"); property("margin-bottom", "0.25rem") } }) {
        Button({
            onClick {
                val target = !allExpanded
                byPerson.forEach { (uid, _) -> expandedIds[uid] = target }
            }
            style {
                property("background", "transparent"); property("border", "1px solid var(--rp-hl-med)")
                property("border-radius", "8px"); property("padding", "0.35rem 0.75rem")
                property("cursor", "pointer"); property("font-size", "0.78rem")
                property("font-weight", "600"); property("color", "var(--rp-subtle)")
                property("font-family", "inherit"); property("display", "flex")
                property("align-items", "center"); property("gap", "0.35rem")
            }
        }) {
            FaIcon(if (allExpanded) "fa-solid fa-chevron-up" else "fa-solid fa-chevron-down", size = "0.75rem", color = "var(--rp-subtle)")
            Text(if (allExpanded) "Fäll ihop alla" else "Expandera alla")
        }
    }

    Div({ style { property("display", "flex"); property("flex-direction", "column"); property("gap", "0.75rem") } }) {
        byPerson.forEach { (userId, personRecs) ->
            val firstRec = personRecs.first()
            val name = listOfNotNull(firstRec.firstName, firstRec.lastName).joinToString(" ").ifEmpty { firstRec.userEmail }
            val initials = name.split(" ").mapNotNull { it.firstOrNull()?.uppercaseChar() }.take(2).joinToString("")
            val totalMin = personRecs.sumOf { it.netMinutes }
            val totalH = totalMin / 60
            val totalM = totalMin % 60
            val totalLabel = "${totalH}h${if (totalM > 0) "${totalM}m" else ""}"
            val daysCount = personRecs.map { it.day }.distinct().size
            val unapproved = personRecs.filter { !it.approved }
            val expanded = expandedIds[userId] == true

            Div({ style {
                property("background", "var(--rp-surface)"); property("border-radius", "16px")
                property("overflow", "hidden"); property("border", "1px solid var(--rp-hl-med)")
            } }) {
                // Header row
                Div({
                    onClick { expandedIds[userId] = !expanded }
                    style {
                        property("display", "flex"); property("align-items", "center"); property("gap", "0.75rem")
                        property("padding", "1rem 1.25rem"); property("cursor", "pointer")
                        property("background", if (expanded) "var(--rp-surface)" else "var(--rp-surface)")
                    }
                }) {
                    // Avatar
                    val colorScheme = getEmployeeColorScheme(null, userId)
                    Div({ style {
                        property("width", "36px"); property("height", "36px"); property("border-radius", "50%")
                        property("background", colorScheme.bg); property("display", "flex")
                        property("align-items", "center"); property("justify-content", "center")
                        property("font-weight", "800"); property("font-size", "0.75rem"); property("color", colorScheme.text)
                        property("border", "2px solid ${colorScheme.primary}")
                        property("flex-shrink", "0")
                    } }) { Text(initials.ifEmpty { "?" }) }

                    // Name + meta
                    Div({ style { property("flex", "1"); property("min-width", "0") } }) {
                        Div({ style {
                            property("font-weight", "700"); property("font-size", "0.95rem"); property("color", "var(--rp-text)")
                            property("white-space", "nowrap"); property("overflow", "hidden"); property("text-overflow", "ellipsis")
                        } }) { Text(name) }
                        Div({ style {
                            property("display", "flex"); property("gap", "0.5rem"); property("margin-top", "0.2rem")
                            property("flex-wrap", "wrap")
                        } }) {
                            Span({ style {
                                property("font-size", "0.75rem"); property("font-weight", "700"); property("color", "var(--rp-pine)")
                                property("background", "var(--rp-info-bg)"); property("padding", "0.15rem 0.5rem"); property("border-radius", "20px")
                            } }) { Text(totalLabel) }
                            Span({ style {
                                property("font-size", "0.75rem"); property("color", "var(--rp-subtle)")
                                property("background", "var(--rp-overlay)"); property("padding", "0.15rem 0.5rem"); property("border-radius", "20px")
                            } }) { Text("$daysCount dagar") }
                            if (personRecs.all { it.approved }) {
                                Span({ style {
                                    property("font-size", "0.75rem"); property("color", "var(--rp-ok-text)")
                                    property("background", "var(--rp-ok-bg)"); property("padding", "0.15rem 0.5rem"); property("border-radius", "20px")
                                } }) { Text("Alla godkända") }
                            }
                        }
                    }

                    // Approve-all button
                    if (unapproved.isNotEmpty()) {
                        Button({
                            onClick { ev ->
                                ev.stopPropagation()
                                monthViewScope.launch {
                                    personRecs.filter { !it.approved }.forEach { rec ->
                                        payrollRepo.updateRecord(rec.id, UpdatePayrollRecordRequest(approved = true))
                                    }
                                    onReload()
                                }
                            }
                            style {
                                property("background", "var(--rp-ok-bg)"); property("border", "1px solid var(--rp-ok-border)")
                                property("border-radius", "8px"); property("padding", "0.4rem 0.8rem")
                                property("color", "var(--rp-ok-text)"); property("font-size", "0.78rem")
                                property("font-weight", "700"); property("cursor", "pointer")
                                property("display", "flex"); property("align-items", "center"); property("gap", "0.4rem")
                                property("flex-shrink", "0")
                            }
                        }) {
                            FaIcon("fa-regular fa-circle-check", size = "0.8rem", color = "var(--rp-ok-text)")
                            Text("Godkänn alla (${unapproved.size})")
                        }
                    }

                    // Expand chevron
                    FaIcon(if (expanded) "fa-solid fa-chevron-up" else "fa-solid fa-chevron-down", size = "0.8rem")
                }

                // Expanded rows
                if (expanded) {
                    Div({ style {
                        property("border-top", "1px solid var(--rp-hl-med)")
                    } }) {
                        personRecs.sortedBy { it.day }.forEach { rec ->
                            val dateParts = rec.day.split("-")
                            val dayNum = dateParts[2].toInt()
                            val monthIdx = dateParts[1].toInt() - 1
                            val dayOfWeek = run {
                                val d = parseDate(rec.day)
                                DAY_NAMES_SHORT[(d.getDay() + 6) % 7]
                            }
                            val netH = rec.netMinutes / 60
                            val netM = rec.netMinutes % 60
                            val netLabel = "${netH}h${if (netM > 0) "${netM}m" else ""}"

                            Div({ style {
                                if (isMobile) {
                                    property("display", "flex")
                                    property("flex-direction", "column")
                                    property("gap", "0.5rem")
                                    property("padding", "0.75rem 1rem")
                                } else {
                                    property("display", "flex")
                                    property("align-items", "center")
                                    property("gap", "0.75rem")
                                    property("padding", "0.7rem 1.25rem")
                                }
                                property("background", if (rec.approved) "var(--rp-ok-bg)" else "transparent")
                                property("border-bottom", "1px solid var(--rp-hl-med)")
                            } }) {
                                if (isMobile) {
                                    // Mobile top bar: Date + Net hours + Status + Quick actions
                                    Div({ style {
                                        property("display", "flex")
                                        property("align-items", "center")
                                        property("justify-content", "space-between")
                                        property("width", "100%")
                                        property("gap", "0.5rem")
                                    } }) {
                                        Div({ style {
                                            property("font-size", "0.82rem"); property("color", "var(--rp-subtle)")
                                        } }) {
                                            Span({ style { property("font-weight", "700"); property("color", "var(--rp-text)") } }) { Text(dayOfWeek) }
                                            Text(" $dayNum ${MONTH_NAMES[monthIdx].take(3)}")
                                        }

                                        Div({ style {
                                            property("display", "flex"); property("align-items", "center"); property("gap", "0.4rem")
                                        } }) {
                                            Span({ style {
                                                property("font-weight", "800"); property("font-size", "0.82rem")
                                                property("color", "var(--rp-text)")
                                            } }) { Text(netLabel) }

                                            Span({ style {
                                                property("padding", "0.15rem 0.45rem"); property("border-radius", "20px")
                                                property("font-size", "0.65rem"); property("font-weight", "700")
                                                if (rec.approved) { property("background", "var(--rp-ok-bg)"); property("color", "var(--rp-pine)") }
                                                else { property("background", "var(--rp-warn-bg)"); property("color", "var(--rp-gold)") }
                                            } }) { Text(if (rec.approved) "Godkänd" else "Väntar") }

                                            if (rec.approved) {
                                                Button({
                                                    onClick {
                                                        monthViewScope.launch {
                                                            payrollRepo.updateRecord(rec.id, UpdatePayrollRecordRequest(approved = false))
                                                            onReload()
                                                        }
                                                    }
                                                    attr("title", "Lås upp för redigering")
                                                    style {
                                                        property("background", "var(--rp-overlay)"); property("border", "none")
                                                        property("border-radius", "8px"); property("padding", "0.25rem 0.5rem")
                                                        property("cursor", "pointer"); property("color", "var(--rp-subtle)")
                                                        property("display", "inline-flex"); property("align-items", "center"); property("gap", "0.25rem")
                                                        property("font-size", "0.7rem"); property("font-weight", "600")
                                                    }
                                                }) {
                                                    FaIcon("fa-solid fa-lock", size = "0.7rem")
                                                    Text("Lås upp")
                                                }
                                            }
                                        }
                                    }

                                    // Timeline bar underneath with full width
                                    Div({ style { property("width", "100%"); property("padding-top", "0.2rem") } }) {
                                        PayrollTimelineBar(
                                            rec = rec,
                                            drag = drag,
                                            onDragStart = onDragStart,
                                            onDragUpdate = onDragUpdate,
                                            onDragEnd = onDragEnd,
                                            isMobile = true
                                        )
                                    }

                                    // Mobile unapproved action buttons row
                                    if (!rec.approved) {
                                        val schedStart = rec.slotStartTime?.take(5)
                                        val schedEnd = rec.slotEndTime?.take(5)
                                        val ciStr = rec.checkInTime?.take(5)
                                        val coStr = rec.checkOutTime?.take(5)

                                        Div({ style {
                                            property("display", "flex"); property("align-items", "center")
                                            property("gap", "0.4rem"); property("flex-wrap", "wrap")
                                            property("margin-top", "0.25rem")
                                        } }) {
                                            if (schedStart != null && schedEnd != null) {
                                                val breakToApply = rec.slotBreakMinutes ?: rec.breakMinutes
                                                Button({
                                                    onClick {
                                                        monthViewScope.launch {
                                                            payrollRepo.updateRecord(rec.id, UpdatePayrollRecordRequest(
                                                                startTime = rec.slotStartTime,
                                                                endTime = rec.slotEndTime,
                                                                breakMinutes = breakToApply,
                                                                approved = true
                                                            ))
                                                            onReload()
                                                        }
                                                    }
                                                    attr("title", "Godkänn med passets schemalagda tid ($schedStart–$schedEnd)")
                                                    style {
                                                        property("background", "var(--rp-ok-bg)"); property("color", "var(--rp-pine)")
                                                        property("border", "1px solid var(--rp-ok-border)"); property("border-radius", "8px")
                                                        property("padding", "0.3rem 0.55rem"); property("font-size", "0.72rem")
                                                        property("font-weight", "700"); property("cursor", "pointer")
                                                        property("display", "inline-flex"); property("align-items", "center"); property("gap", "0.3rem")
                                                    }
                                                }) {
                                                    FaIcon("fa-regular fa-circle-check", size = "0.7rem", color = "var(--rp-ok-text)")
                                                    Text("Passet $schedStart–$schedEnd")
                                                }
                                            }

                                            if (ciStr != null && coStr != null) {
                                                Button({
                                                    onClick {
                                                        monthViewScope.launch {
                                                            payrollRepo.updateRecord(rec.id, UpdatePayrollRecordRequest(
                                                                startTime = rec.checkInTime,
                                                                endTime = rec.checkOutTime,
                                                                approved = true
                                                            ))
                                                            onReload()
                                                        }
                                                    }
                                                    attr("title", "Godkänn med stämplad tid ($ciStr–$coStr)")
                                                    style {
                                                        property("background", "var(--rp-warn-bg)"); property("color", "var(--rp-gold)")
                                                        property("border", "1px solid var(--rp-warn-border)"); property("border-radius", "8px")
                                                        property("padding", "0.3rem 0.55rem"); property("font-size", "0.72rem")
                                                        property("font-weight", "700"); property("cursor", "pointer")
                                                        property("display", "inline-flex"); property("align-items", "center"); property("gap", "0.3rem")
                                                    }
                                                }) {
                                                    FaIcon("fa-solid fa-clock", size = "0.7rem", color = "var(--rp-warn-text)")
                                                    Text("Stämplat $ciStr–$coStr")
                                                }
                                            }

                                            Button({
                                                onClick { onEditRecord(rec) }
                                                attr("title", "Anpassa tid, rast eller notering")
                                                style {
                                                    property("background", "var(--rp-overlay)"); property("border", "1px solid var(--rp-hl-med)")
                                                    property("border-radius", "8px"); property("padding", "0.3rem 0.55rem")
                                                    property("cursor", "pointer"); property("color", "var(--rp-subtle)")
                                                    property("font-size", "0.72rem"); property("font-weight", "600")
                                                    property("display", "inline-flex"); property("align-items", "center"); property("gap", "0.25rem")
                                                }
                                            }) {
                                                FaIcon("fa-solid fa-pen", size = "0.65rem", color = "var(--rp-subtle)")
                                                Text("Justera")
                                            }
                                        }
                                    }
                                } else {
                                    // Desktop row layout
                                    // Day label
                                    Div({ style {
                                        property("width", "80px"); property("flex-shrink", "0")
                                        property("font-size", "0.78rem"); property("color", "var(--rp-subtle)")
                                    } }) {
                                        Span({ style { property("font-weight", "700"); property("color", "var(--rp-text)") } }) { Text(dayOfWeek) }
                                        Text(" $dayNum ${MONTH_NAMES[monthIdx].take(3)}")
                                    }

                                    // Timeline bar
                                    Div({ style { property("flex", "1"); property("min-width", "0") } }) {
                                        PayrollTimelineBar(
                                            rec = rec,
                                            drag = drag,
                                            onDragStart = onDragStart,
                                            onDragUpdate = onDragUpdate,
                                            onDragEnd = onDragEnd,
                                            isMobile = false
                                        )
                                    }

                                    // Net hours & Break badge
                                    Div({ style {
                                        property("display", "flex"); property("flex-direction", "column")
                                        property("align-items", "flex-end"); property("width", "60px")
                                        property("flex-shrink", "0")
                                    } }) {
                                        Span({ style {
                                            property("font-weight", "800"); property("font-size", "0.85rem")
                                            property("color", "var(--rp-text)"); property("white-space", "nowrap")
                                        } }) { Text(netLabel) }

                                        if (rec.breakMinutes > 0) {
                                            Span({ style {
                                                property("font-size", "0.65rem"); property("font-weight", "600")
                                                property("color", "var(--rp-subtle)"); property("white-space", "nowrap")
                                            } }) { Text("${rec.breakMinutes}m rast") }
                                        }
                                    }

                                    // Status chip
                                    Span({ style {
                                        property("padding", "0.2rem 0.55rem"); property("border-radius", "20px")
                                        property("font-size", "0.68rem"); property("font-weight", "700")
                                        property("flex-shrink", "0")
                                        if (rec.approved) { property("background", "var(--rp-ok-bg)"); property("color", "var(--rp-pine)") }
                                        else { property("background", "var(--rp-warn-bg)"); property("color", "var(--rp-gold)") }
                                    } }) { Text(if (rec.approved) "Godkänd" else "Väntar") }

                                    // Quick action buttons container (no shrink, clean gap)
                                    Div({ style {
                                        property("display", "flex"); property("align-items", "center")
                                        property("gap", "0.4rem"); property("flex-shrink", "0")
                                    } }) {
                                        if (!rec.approved) {
                                            val schedStart = rec.slotStartTime?.take(5)
                                            val schedEnd = rec.slotEndTime?.take(5)
                                            val ciStr = rec.checkInTime?.take(5)
                                            val coStr = rec.checkOutTime?.take(5)

                                            // GREEN: Schemalagt pass (primary/recommended)
                                            if (schedStart != null && schedEnd != null) {
                                                val breakToApply = rec.slotBreakMinutes ?: rec.breakMinutes
                                                Button({
                                                    onClick {
                                                        monthViewScope.launch {
                                                            payrollRepo.updateRecord(rec.id, UpdatePayrollRecordRequest(
                                                                startTime = rec.slotStartTime,
                                                                endTime = rec.slotEndTime,
                                                                breakMinutes = breakToApply,
                                                                approved = true
                                                            ))
                                                            onReload()
                                                        }
                                                    }
                                                    attr("title", "Godkänn med passets schemalagda tid ($schedStart–$schedEnd" + (if (breakToApply > 0) ", ${breakToApply}m rast" else "") + ")")
                                                    style {
                                                        property("background", "var(--rp-ok-bg)"); property("color", "var(--rp-pine)")
                                                        property("border", "1px solid var(--rp-ok-border)"); property("border-radius", "8px")
                                                        property("padding", "0.35rem 0.65rem"); property("font-size", "0.75rem")
                                                        property("font-weight", "700"); property("cursor", "pointer")
                                                        property("white-space", "nowrap"); property("display", "inline-flex")
                                                        property("align-items", "center"); property("gap", "0.35rem")
                                                    }
                                                }) {
                                                    FaIcon("fa-regular fa-circle-check", size = "0.75rem", color = "var(--rp-ok-text)")
                                                    Text("Passet $schedStart–$schedEnd")
                                                }
                                            }

                                            // ORANGE/AMBER: Stämplad in-/utcheckning (avvikelse)
                                            if (ciStr != null && coStr != null) {
                                                Button({
                                                    onClick {
                                                        monthViewScope.launch {
                                                            payrollRepo.updateRecord(rec.id, UpdatePayrollRecordRequest(
                                                                startTime = rec.checkInTime,
                                                                endTime = rec.checkOutTime,
                                                                approved = true
                                                            ))
                                                            onReload()
                                                        }
                                                    }
                                                    attr("title", "Godkänn med stämplad tid ($ciStr–$coStr)")
                                                    style {
                                                        property("background", "var(--rp-warn-bg)"); property("color", "var(--rp-gold)")
                                                        property("border", "1px solid var(--rp-warn-border)"); property("border-radius", "8px")
                                                        property("padding", "0.35rem 0.65rem"); property("font-size", "0.75rem")
                                                        property("font-weight", "700"); property("cursor", "pointer")
                                                        property("white-space", "nowrap"); property("display", "inline-flex")
                                                        property("align-items", "center"); property("gap", "0.35rem")
                                                    }
                                                }) {
                                                    FaIcon("fa-solid fa-clock", size = "0.75rem", color = "var(--rp-warn-text)")
                                                    Text("Stämplat $ciStr–$coStr")
                                                }
                                            }

                                            // GRAY: Anpassa manuellt (öppnar modal med presets)
                                            Button({
                                                onClick { onEditRecord(rec) }
                                                attr("title", "Anpassa tid, rast eller notering")
                                                style {
                                                    property("background", "var(--rp-overlay)"); property("border", "1px solid var(--rp-hl-med)")
                                                    property("border-radius", "8px"); property("padding", "0.35rem 0.65rem")
                                                    property("cursor", "pointer"); property("color", "var(--rp-subtle)")
                                                    property("font-size", "0.75rem"); property("font-weight", "600")
                                                    property("white-space", "nowrap"); property("display", "inline-flex")
                                                    property("align-items", "center"); property("gap", "0.3rem")
                                                }
                                            }) {
                                                FaIcon("fa-solid fa-pen", size = "0.7rem", color = "var(--rp-subtle)")
                                                Text("Justera")
                                            }
                                        } else {
                                            // Unlock button
                                            Button({
                                                onClick {
                                                    monthViewScope.launch {
                                                        payrollRepo.updateRecord(rec.id, UpdatePayrollRecordRequest(approved = false))
                                                        onReload()
                                                    }
                                                }
                                                attr("title", "Lås upp för redigering")
                                                style {
                                                    property("background", "var(--rp-overlay)"); property("border", "none")
                                                    property("border-radius", "8px"); property("padding", "0.35rem 0.6rem")
                                                    property("cursor", "pointer"); property("color", "var(--rp-subtle)")
                                                    property("display", "inline-flex"); property("align-items", "center"); property("gap", "0.3rem")
                                                    property("font-size", "0.72rem"); property("font-weight", "600")
                                                }
                                            }) {
                                                FaIcon("fa-solid fa-lock", size = "0.72rem")
                                                Text("Lås upp")
                                            }
                                        }
                                    }
                                }
                            }
                        }
                    }
                }
            }
        }
    }
}
